import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useTheme } from '../App';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  Sun,
  Moon,
  LogOut,
  Settings,
  ShieldCheck,
  TrendingUp,
  ShoppingCart,
  DollarSign,
  Luggage,
  LayoutGrid,
  Repeat,
  Headphones
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import NotificationCenter from './NotificationCenter';
import SupportContact from './SupportContact';

const Navbar = ({ transparent = false }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      transparent ? 'bg-transparent' : 'glass border-b'
    }`}>
      <nav className="max-w-7xl mx-auto px-1 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">

          {/* Logo */}
          <Link to="/hub" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_60b0b1ea-0b06-47a8-bc3e-0e0d910144d6/artifacts/xd1x3snr_kacos-logo-final-transparent.svg"
              alt="Kacos Logo"
              className="h-7 sm:h-10 w-auto"
            />
          </Link>

          {/* Center nav */}
          <div className="flex items-center gap-1.5 sm:gap-6">
            {/* Hub — desktop only */}
            <Link
              to="/hub"
              className="hidden md:flex items-center gap-1.5 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
              data-testid="nav-link-hub"
            >
              <LayoutGrid className="w-4 h-4" />
              Hub
            </Link>

            {/* Bagages — always visible */}
            <Link
              to="/listings"
              className="flex items-center gap-1 text-xs sm:text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
              data-testid="nav-link-bagages"
            >
              <Luggage className="w-4 h-4" />
              <span>Bagages</span>
            </Link>

            {/* Devises — always visible */}
            <Link
              to="/currency/listings"
              className="flex items-center gap-1 text-xs sm:text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
              data-testid="nav-link-devises"
            >
              <DollarSign className="w-4 h-4" />
              <span>Devises</span>
            </Link>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-0.5 sm:gap-2">
            {/* Theme toggle — desktop only */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className="hidden sm:flex rounded-full"
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>

            {user ? (
              <>
                {/* Support — always visible for logged-in users */}
                <SupportContact
                  triggerButton={
                    <Button variant="ghost" size="icon" className="rounded-full" data-testid="nav-support-btn">
                      <Headphones className="h-5 w-5" />
                    </Button>
                  }
                />

                {/* Notifications — always visible */}
                <NotificationCenter />

                {/* User dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="rounded-full gap-2 px-1.5 sm:px-3" data-testid="user-menu-trigger">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center shrink-0">
                        <span className="text-white text-sm font-medium">
                          {user.first_name?.[0] || 'U'}
                        </span>
                      </div>
                      <span className="hidden sm:inline text-sm">{user.first_name}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-1.5">
                      <p className="text-sm font-medium">{user.first_name} {user.last_name}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                      <Badge variant="outline" className="mt-1 text-xs">{user.grade}</Badge>
                    </div>
                    <DropdownMenuSeparator />
                    <div className="px-2 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Bagages</div>
                    <DropdownMenuItem onClick={() => navigate('/seller')} data-testid="nav-seller">
                      <TrendingUp className="mr-2 h-4 w-4" />
                      Vendeur Bagages
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/buyer')} data-testid="nav-buyer">
                      <ShoppingCart className="mr-2 h-4 w-4" />
                      Acheteur Bagages
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <div className="px-2 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Devises</div>
                    <DropdownMenuItem onClick={() => navigate('/currency/listings')} data-testid="nav-currency-exchange">
                      <DollarSign className="mr-2 h-4 w-4 text-emerald-500" />
                      Échanger de l'argent
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/currency/transactions')} data-testid="nav-currency-transactions">
                      <Repeat className="mr-2 h-4 w-4 text-emerald-500" />
                      Mes transactions
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="nav-settings">
                      <Settings className="mr-2 h-4 w-4" />
                      Paramètres
                    </DropdownMenuItem>
                    {user.is_admin && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="nav-admin">
                        <ShieldCheck className="mr-2 h-4 w-4" />
                        Administration
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="nav-logout">
                      <LogOut className="mr-2 h-4 w-4" />
                      Déconnexion
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-0.5 sm:gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/login')}
                  data-testid="login-btn"
                  className="text-xs sm:text-sm px-1.5 sm:px-4 h-8 sm:h-9"
                >
                  Connexion
                </Button>
                <Button
                  onClick={() => navigate('/register')}
                  data-testid="register-btn"
                  size="sm"
                  className="btn-primary text-xs sm:text-sm px-1.5 sm:px-4 h-8 sm:h-9"
                >
                  <span className="sm:hidden">S'inscrire</span>
                  <span className="hidden sm:inline">Inscription</span>
                </Button>
              </div>
            )}
          </div>

        </div>
      </nav>
    </header>
  );
};

export default Navbar;
